import { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, CircleDollarSign, LogOut, Moon, ShieldCheck, Sun, TrendingUp, Users } from 'lucide-react';
import { AuthContext } from '../../context/Auth';
import { useTheme } from '../../hooks/useTheme';

const navigation = [
  { icon: Users, to: '/dashboard', title: 'Usuários' },
  { icon: CircleDollarSign, to: '/sales', title: 'Vendas' },
  { icon: ShieldCheck, to: '/collaborator', title: 'Colaboradores' },
  { icon: BarChart3, to: '/reports', title: 'Relatórios' },
  { icon: TrendingUp, to: '/growth', title: 'Crescimento' },
];

const idle = 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-[#0e0e10] dark:text-neutral-400 dark:hover:bg-[#18181b] dark:hover:text-neutral-300';
const active = 'bg-green-500 text-white shadow-lg shadow-green-500/50';

// Menu flutuante igual ao da dashboard: na lateral no computador e uma pílula no topo no celular
function Menu({ mobile = false }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { signOut } = useContext(AuthContext);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const btn = mobile ? 'size-10' : 'sidebar-btn';
  const icon = mobile ? 'size-5' : 'sidebar-icon';

  const handleSignOut = () => {
    signOut();
    navigate('/');
  };

  return (
    <>
      <nav className={mobile ? 'flex items-center gap-1' : 'sidebar-gap no-scrollbar flex min-h-0 flex-col overflow-y-auto'}>
        {navigation.map(({ icon: Icon, to, title }) => {
          const isActive = pathname === to;
          return (
            <Link key={to} to={to} title={title} className={`flex ${btn} shrink-0 items-center justify-center rounded-full transition-all duration-200 ${isActive ? active : mobile ? 'text-neutral-500 dark:text-neutral-400' : idle}`}>
              <Icon className={icon} strokeWidth={isActive ? 2.5 : 2} />
            </Link>
          );
        })}
      </nav>
      <span className={mobile ? 'mx-1 h-6 w-px bg-neutral-200 dark:bg-neutral-800' : 'h-px w-8 shrink-0 bg-neutral-300 dark:bg-neutral-700'} />
      <button onClick={toggleDarkMode} title={isDarkMode ? 'Modo claro' : 'Modo escuro'} className={`flex ${btn} shrink-0 items-center justify-center rounded-full transition-all duration-200 ${mobile ? 'text-neutral-500 dark:text-neutral-400' : idle}`}>
        {isDarkMode ? <Sun className={icon} /> : <Moon className={icon} />}
      </button>
      <button onClick={handleSignOut} title="Sair" className={`flex ${btn} shrink-0 items-center justify-center rounded-full transition-all duration-200 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400 ${mobile ? 'text-neutral-500 dark:text-neutral-400' : idle}`}>
        <LogOut className={icon} />
      </button>
    </>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen">
      <div className="fixed left-4 top-1/2 z-30 hidden -translate-y-1/2 lg:block">
        <div className="sidebar-gap sidebar-pad flex max-h-[calc(100vh-2rem)] flex-col items-center rounded-2xl border border-neutral-200 bg-white/90 px-2 shadow-xl backdrop-blur-md dark:border-neutral-800 dark:bg-[#050505]">
          <Menu />
        </div>
      </div>
      <div className="fixed left-1/2 top-4 z-30 -translate-x-1/2 lg:hidden">
        <div className="flex items-center rounded-full border border-neutral-200 bg-white/90 px-3 py-2 shadow-xl backdrop-blur-md dark:border-neutral-800 dark:bg-[#050505]">
          <Menu mobile />
        </div>
      </div>
      <main className="pt-20 lg:ml-24 lg:pt-0">{children}</main>
    </div>
  );
}
